import SensorsIcon from '@mui/icons-material/Sensors'
import {Box, Skeleton, Tooltip, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import type {FC} from 'react'
import {useGetIngestLagSummaryQuery} from '@apis/signals'
import {formatGapDuration} from '@shared/utils/signalGaps'
import {useCustomTranslation} from '@hooks/useCustomTranslation'

// p95 thresholds (seconds) for the health colour. Prod runs ~80–110s p95, so a
// 5-minute green band comfortably covers healthy ingest; amber (≤15 min) / red
// flag a genuinely lagging pipeline.
const GREEN_MAX_SECONDS = 300
const AMBER_MAX_SECONDS = 900

// Tenant-wide data-freshness indicator (AB#5519): how long, typically, from a
// reading being generated to it being queryable. Lives in the global app bar as
// a system-status signal — QUIET when healthy (a lone green sensor dot), LOUD
// when lagging (a tinted "Data delayed …" pill). Hidden while loading or when
// there are no live signals to summarise.
const IngestLagChip: FC = () => {
  const [translate] = useCustomTranslation('common')
  const t = (key: string, opts?: Record<string, unknown>): string => String(translate(key, opts))
  const theme = useTheme()
  // Live-ish indicator: refetch every 60s while the signals page is open (RTK stops
  // polling once the chip unmounts), and refresh on each revisit. No tag invalidation
  // is wired — the summary is time-based, not mutated by any user action.
  const {data, isLoading} = useGetIngestLagSummaryQuery(undefined, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
  })

  // First load: hold the dot's footprint with a skeleton so the app bar doesn't
  // reflow when the summary lands.
  if (isLoading) {
    return <Skeleton variant="circular" width={18} height={18} sx={{mx: 1}} />
  }

  if (!data || data.sample_size === 0 || data.p50_seconds == null) {
    return null
  }

  const p95 = data.p95_seconds ?? data.p50_seconds
  const severity: 'success' | 'warning' | 'error' =
    p95 <= GREEN_MAX_SECONDS ? 'success' : p95 <= AMBER_MAX_SECONDS ? 'warning' : 'error'
  const healthy = severity === 'success'
  const color = theme.palette[severity].main
  const statusKey =
    severity === 'success'
      ? 'signals.ingest_lag_status_ok'
      : severity === 'warning'
        ? 'signals.ingest_lag_status_elevated'
        : 'signals.ingest_lag_status_high'

  const tooltip = (
    <Box>
      {/* Plain-language verdict first so the number doesn't need decoding. */}
      <Typography variant="caption" display="block" sx={{fontWeight: 600, color}}>
        {t(statusKey)}
      </Typography>
      <Typography variant="caption" display="block" sx={{mt: 0.5}}>
        {t('signals.ingest_lag_tooltip_desc', {p50: formatGapDuration(data.p50_seconds)})}
      </Typography>
      <Typography variant="caption" display="block" sx={{mt: 0.5}}>
        {t('signals.ingest_lag_tooltip_stats', {
          p95: formatGapDuration(p95),
          count: data.sample_size.toLocaleString(),
        })}
      </Typography>
    </Box>
  )

  return (
    <Tooltip arrow title={tooltip}>
      <Box
        data-testid="ingest-lag-chip"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: 1,
          cursor: 'default',
          // Quiet when healthy (a lone sensor dot); a tinted pill spelling out
          // the delay only when ingest is actually lagging.
          ...(healthy ? {p: 1} : {px: 1, py: 0.25, bgcolor: alpha(color, 0.12)}),
        }}
      >
        <SensorsIcon sx={{fontSize: 18, color}} />
        {healthy ? null : (
          <Typography variant="caption" sx={{color, whiteSpace: 'nowrap', fontWeight: 500}}>
            {t('signals.data_delayed', {duration: formatGapDuration(data.p50_seconds)})}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

export default IngestLagChip
